import { Download, Users, FileText, ClipboardCheck, Video, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { downloadCsv, csvFilename } from '@/lib/csv';
import { useOrgBatchAssignments, useOrgBatchAssessments, useOrgBatchSessions } from '@/lib/hooks/useOrg';
import type { OrgBatchDetail } from '@/lib/api/org';

const EXPORT_BUTTON = 'inline-flex items-center gap-2 rounded-[14px] border border-[#ececec] bg-white px-4 py-2.5 text-sm font-medium text-[#111827] disabled:cursor-not-allowed disabled:opacity-40 hover:enabled:bg-[#fafafa]';

interface ReportCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  disabled: boolean;
  onDownload: () => void;
}

function ReportCard({ icon: Icon, title, description, disabled, onDownload }: ReportCardProps) {
  return (
    <OrgCard className="flex flex-col gap-4 p-6">
      <div className="flex items-start gap-3">
        <span className="flex h-[42px] w-[42px] flex-shrink-0 items-center justify-center rounded-[14px] bg-[#fff4f0]">
          <Icon className="h-5 w-5 text-[#ff5a1f]" />
        </span>
        <div>
          <h3 className="text-sm font-semibold text-[#111827]">{title}</h3>
          <p className="mt-0.5 text-sm text-[#6b7280]">{description}</p>
        </div>
      </div>
      <button type="button" onClick={onDownload} disabled={disabled} className={cn(EXPORT_BUTTON, 'self-start')}>
        <Download className="h-4 w-4" /> Download CSV
      </button>
    </OrgCard>
  );
}

export function BatchReportsTab({ batchId, data }: { batchId: string; data: OrgBatchDetail }) {
  const assignments = useOrgBatchAssignments(batchId);
  const assessments = useOrgBatchAssessments(batchId);
  const sessions = useOrgBatchSessions(batchId);

  const downloadStudents = () => {
    const header = ['Name', 'Email', 'Completion %', 'Attendance %', 'Avg score', 'Status', 'Topics'];
    const rows = data.students.map((s) => [
      s.name,
      s.email,
      Math.round(s.completion_pct),
      Math.round(s.attendance_pct),
      Math.round(s.avg_score),
      s.enrollment_status,
      `${s.completed_topics}/${s.total_topics}`,
    ]);
    downloadCsv(csvFilename(data.batch.name, 'students'), header, rows);
  };

  const downloadAssignments = () => {
    const rows = assignments.data ?? [];
    const header = ['Title', 'Kind', 'Due Date', 'Submitted', 'Total Students', 'Avg Score %', 'Overdue'];
    const body = rows.map((a) => [
      a.title,
      a.kind,
      a.due_date ?? '',
      a.submitted_count,
      a.total_students,
      Math.round(a.max_score ? (a.avg_score / a.max_score) * 100 : 0),
      a.overdue ? 'Yes' : 'No',
    ]);
    downloadCsv(csvFilename(data.batch.name, 'assignments'), header, body);
  };

  const downloadAssessments = () => {
    const rows = assessments.data ?? [];
    const header = ['Title', 'Kind', 'Due Date', 'Submitted', 'Total Students', 'Avg Score %', 'Overdue'];
    const body = rows.map((a) => [
      a.title,
      a.kind,
      a.due_date ?? '',
      a.submitted_count,
      a.total_students,
      Math.round(a.avg_score_pct),
      a.overdue ? 'Yes' : 'No',
    ]);
    downloadCsv(csvFilename(data.batch.name, 'assessments'), header, body);
  };

  const downloadSessions = () => {
    const rows = sessions.data ?? [];
    const header = ['Title', 'Type', 'Scheduled At', 'Status', 'Present Count', 'Total Students'];
    const body = rows.map((s) => [
      s.title,
      s.session_type,
      s.scheduled_at ?? '',
      s.status,
      s.present_count,
      s.total_students,
    ]);
    downloadCsv(csvFilename(data.batch.name, 'sessions'), header, body);
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-[#111827]">Reports</h2>
        <p className="text-sm text-[#6b7280]">Download CSV exports for this batch.</p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <ReportCard
          icon={Users}
          title="Student Roster"
          description="Every student's completion, attendance, score, and topic progress."
          disabled={data.students.length === 0}
          onDownload={downloadStudents}
        />
        <ReportCard
          icon={FileText}
          title="Assignments Summary"
          description="Submission rate and average score for every assignment and project."
          disabled={assignments.isLoading || assignments.isError || (assignments.data?.length ?? 0) === 0}
          onDownload={downloadAssignments}
        />
        <ReportCard
          icon={ClipboardCheck}
          title="Assessments Summary"
          description="Submission rate and average score for every quiz and coding challenge."
          disabled={assessments.isLoading || assessments.isError || (assessments.data?.length ?? 0) === 0}
          onDownload={downloadAssessments}
        />
        <ReportCard
          icon={Video}
          title="Sessions & Attendance"
          description="Schedule, status, and attendance count for every session."
          disabled={sessions.isLoading || sessions.isError || (sessions.data?.length ?? 0) === 0}
          onDownload={downloadSessions}
        />
      </div>
    </div>
  );
}
